import CheckIcon from '@mui/icons-material/Check';
import { Box, Chip, Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

import { useAppDispatch } from '../../../../lib/store';
import {
  useGetAttributesQuery,
  useGetMandatoryAttributeNamesQuery,
} from '../../api/createProjectApiSlice';
import {
  AddedConcept,
  addRequiredAttribute,
  removeRequiredAttribute,
} from '../../state/createProjectSlice';
import type { AttributeResponse } from '../../types/response';

interface UseAttributeSelectionProps {
  concept: AddedConcept;
}

interface UseAttributeSelectionResults {
  selectedAttributeIds: string[];
  handleAddAttribute: (attribute: AttributeResponse) => void;
  handleRemoveAttribute: (attribute: AttributeResponse) => void;
  attributes: AttributeResponse[];
  isAttributesLoading: boolean;
  mandatoryAttributeNames: string[];
  isMandatoryAttributeNamesLoading: boolean;
}
export const useAttributeSelection = ({
  concept,
}: UseAttributeSelectionProps): UseAttributeSelectionResults => {
  const dispatch = useAppDispatch();

  const selectedAttributes = concept.requiredAttributes;
  const selectedAttributeIds = selectedAttributes.map((attr) => attr.id);

  const handleAddAttribute = (attribute: AttributeResponse) => {
    dispatch(
      addRequiredAttribute({
        conceptId: concept.conceptCardId,
        attribute: { id: attribute.id, label: attribute.label },
      })
    );
  };

  const handleRemoveAttribute = (attribute: AttributeResponse) => {
    const attributeToRemove = selectedAttributes.find((attr) => attr.id === attribute.id);
    if (attributeToRemove) {
      dispatch(
        removeRequiredAttribute({
          conceptId: concept.conceptCardId,
          attribute: attributeToRemove,
        })
      );
    }
  };

  const {
    data: attributes,
    isLoading: isAttributesLoading,
    isFetching: isAttributesFetching,
  } = useGetAttributesQuery();
  const {
    data: mandatoryAttributeNames,
    isLoading: isMandatoryAttributeNamesLoading,
    isFetching: isMandatoryAttributeNamesFetching,
  } = useGetMandatoryAttributeNamesQuery();
  return {
    selectedAttributeIds,
    handleAddAttribute,
    handleRemoveAttribute,
    attributes: attributes?.data ?? [],
    isAttributesLoading: isAttributesLoading || isAttributesFetching,
    mandatoryAttributeNames: mandatoryAttributeNames?.data ?? [],
    isMandatoryAttributeNamesLoading:
      isMandatoryAttributeNamesFetching || isMandatoryAttributeNamesLoading,
  };
};
